import { governanceApiClient } from '@/core/infra_api_client'

/**
 * Organisation ingestion connector offering (PLAN-20260911 slice 5 and 6).
 * Each host's ingestion worker answers which connectors it can create and
 * sync; governance-service unions those answers for the organisation's
 * online hosts and applies this organisation-scoped opt-out list. The
 * browser renders, the server decides; no connector list is assembled here.
 */
export type IngestionOfferingAction = 'exclude' | 'include'

export interface IngestionConnectorExclusion {
  connectorType: string
  revision: number
  createdAt: string
}

export interface IngestionConnectorResourceCount {
  connectorType: string
  count: number
}

export interface IngestionOfferingState {
  organizationId: string
  revision: number
  exclusions: IngestionConnectorExclusion[]
}

/** One connector some online host's ingestion worker declared, joined with the organisation's state. */
export interface IngestionOfferingConnector {
  connectorType: string
  displayName: string
  aliases: string[]
  create: boolean
  sync: boolean
  excluded: boolean
  activeResources: number
  hostIds: string[]
}

export type IngestionOfferingHostStatus = 'declared' | 'unsupported' | 'unavailable' | 'unreadable'

/** One connector as a host's worker declares it, with the organisation's verdict. */
export interface IngestionOfferingHostConnector {
  connectorType: string
  displayName: string
  aliases: string[]
  create: boolean
  sync: boolean
  offered: boolean
}

/** One online host of the organisation and whether its worker declared its connectors. */
export interface IngestionOfferingHost {
  hostId: string
  name: string
  intelligenceRuntimeVersion: string
  status: IngestionOfferingHostStatus
  upstreamStatus?: number
  /** The typed reason (not_bound, unreachable, timeout) when unavailable. */
  upstreamReason?: string
  connectorCount: number
  /** The host's own declaration; empty unless status is declared. */
  connectors: IngestionOfferingHostConnector[]
}

export interface IngestionOffering extends IngestionOfferingState {
  /** Server verdict: the mutation routes accept this caller (superusers only). */
  editable: boolean
  /** Union of what the organisation's online hosts declare; the only source of rows. */
  connectors: IngestionOfferingConnector[]
  hosts: IngestionOfferingHost[]
  /** Active governance resources per canonical connector type, declared or not. */
  activeResourceCounts: IngestionConnectorResourceCount[]
}

export interface IngestionOfferingHistoryEntry {
  organizationId: string
  revision: number
  connectorType: string
  action: IngestionOfferingAction
  actorUserId: string
  reason: string
  changedAt: string
}

interface IngestionOfferingHistoryResponse {
  history: IngestionOfferingHistoryEntry[]
}

export interface ChangeIngestionOfferingCommand {
  revision: number
  reason: string
  confirmed: true
}

const offeringPath = 'ingestion-offering'

function connectorPath(connectorType: string): string {
  return `${offeringPath}/${encodeURIComponent(connectorType)}`
}

export function getIngestionOffering(): Promise<IngestionOffering> {
  return governanceApiClient.get<IngestionOffering>(offeringPath)
}

export async function listIngestionOfferingHistory(): Promise<IngestionOfferingHistoryEntry[]> {
  const response = await governanceApiClient.get<IngestionOfferingHistoryResponse>(`${offeringPath}/history`)
  return response.history ?? []
}

export function excludeIngestionConnector(connectorType: string, command: ChangeIngestionOfferingCommand): Promise<IngestionOfferingState> {
  return governanceApiClient.put<IngestionOfferingState, ChangeIngestionOfferingCommand>(connectorPath(connectorType), command)
}

export function includeIngestionConnector(connectorType: string, command: ChangeIngestionOfferingCommand): Promise<IngestionOfferingState> {
  return governanceApiClient.delete<IngestionOfferingState, ChangeIngestionOfferingCommand>(connectorPath(connectorType), command)
}
